import LoadingButton from "@mui/lab/LoadingButton";
import { Box } from "@mui/material";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { EuroField } from "@repo/shared/components";
import {
	useAddServicesToProjectMutation,
	useGetAvailableServicesForProjectLazyQuery,
	useRemoveServicesFromProjectMutation,
} from "@repo/server-api";
import { useCurrencyFormatter, useNotification  } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import React, {
	useState,
	forwardRef,
	useImperativeHandle,
	useMemo,
	type SetStateAction,
} from "react";

import { createEmptyProject, type Project } from "../types/ProjectTypes";
import {
	buildServiceTree,
	computeSelectionDiff,
	getAllServiceIds,
	getServiceIdsInGroup,
	syncParentChildSelection,
} from "../utils/serviceTreeUtils";

import type { SelectionDiff, ServiceTreeNode } from "../types/ServiceTreeTypes";
import type {
	GridCellParams,
	GridColDef,
	GridRowSelectionModel,
} from "@mui/x-data-grid-premium";
import type {
	AvailableService,
	AvailableServiceGroup,
} from "@repo/server-api";


export type DialogSelectServicesArgs = {
	project: Project;
	setProject: (value: SetStateAction<Project>) => void;
};

export type DialogSelectServicesHandle = {
	show: (args: DialogSelectServicesArgs) => Promise<void>;
};
export type DialogSelectServicesProps = Readonly<{
	onSaved: (diff: SelectionDiff) => void;
}>;

const DialogSelectServices = forwardRef<DialogSelectServicesHandle, DialogSelectServicesProps>(
	(props, ref) => {
		const { t } = useTranslation();
		const { showNotification } = useNotification();
		const formatCurrency = useCurrencyFormatter();

		const [open, setOpen] = useState(false);
		const [isLoaded, setIsLoaded] = useState<boolean>(false);
		const [services, setServices] = useState<(AvailableService | AvailableServiceGroup)[]>([]);
		const [saveClicked, setSaveClicked] = useState<boolean>(false);
		const [dialogArgs, setDialogArgs] = useState<DialogSelectServicesArgs>({
			project: createEmptyProject(),
			setProject: () => {},
		});

		const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
			type: "include",
			ids: new Set(),
		});

		const [treeNodes, setTreeNodes] = useState<ServiceTreeNode[]>([]);
		const [selectedTreeIds, setSelectedTreeIds] = useState<Set<string>>(new Set());
		const [initialServiceIds, setInitialServiceIds] = useState<Set<string>>(new Set());

		const [getAllServices] = useGetAvailableServicesForProjectLazyQuery({
			fetchPolicy: "no-cache",
		});

		useImperativeHandle(ref, () => ({
			show: async (args: DialogSelectServicesArgs) => {
				setIsLoaded(false);
				setDialogArgs(args);

				const result = await getAllServices({ variables: { projectId: args.project.id } });
				if (!result || !result.data) return;

				// Build tree structure for TreeView
				const tree = buildServiceTree(result.data.availableServicesForProject);
				setTreeNodes(tree);

				// Get all available service IDs
				const allServiceIds = getAllServiceIds(tree);

				// Flatten for DataGrid (legacy)
				const availableServicesFlat: (AvailableService | AvailableServiceGroup)[] = [];
				result.data.availableServicesForProject.forEach((service: AvailableServiceGroup) => {
					availableServicesFlat.push(service);
					if ("services" in service && service.services.length) {
						service.services.forEach((s: AvailableService) => {
							availableServicesFlat.push(s);
						});
					}
				});

				setServices(availableServicesFlat);

				// Get already assigned service IDs
				const assignedServiceIds = args.project.saleableServices
					.map((s) => s.originalId)
					.filter((id) => allServiceIds.includes(id));

				console.log("SELECTED SERVICES", assignedServiceIds);

				// Set initial selection for TreeView
				const initialSelection = new Set(assignedServiceIds);
				setSelectedTreeIds(syncParentChildSelection(initialSelection, tree));
				setInitialServiceIds(initialSelection);

				// Set row selection for DataGrid (legacy)
				setRowSelectionModel({ type: "include", ids: new Set(assignedServiceIds) });

				setIsLoaded(true);
				setOpen(true);
			},
		}));

		const handleClose = () => {
			setOpen(false);
			setSaveClicked(false);
		};

		const [addServices] = useAddServicesToProjectMutation({
			onCompleted(data) {
				if (data.addServicesToProject.errors) {
					const { errors } = data.addServicesToProject;
					errors.forEach((error) => {
						if ("errors" in error && Array.isArray(error.errors)) {
							if (error.errors.length === 0) showNotification(error.message, "error");
							else
								error.errors?.forEach((e) => {
									showNotification(e.errorMessage, "error");
								});
						} else {
							showNotification(error.message, "error");
						}
					});
				}
			},
			onError(error) {
				showNotification(error.message, "error");
			},
		});

		const [removeServices] = useRemoveServicesFromProjectMutation({
			onCompleted(data) {
				if (data.removeServicesFromProject.project) {
					// TODO REMOVE AND

					props.setProject((prev) => ({
						...prev,
						saleableServiceGroups:
							data.removeServicesFromProject.project.saleableServiceGroups || [],
						saleableServices: data.removeServicesFromProject.project.saleableServices || [],
						saleableUnits: data.removeServicesFromProject.project.saleableUnits || [],
					}));
				} else if (data.removeServicesFromProject.errors) {
					const { errors } = data.removeServicesFromProject;
					errors.forEach((error) => {
						if ("errors" in error && Array.isArray(error.errors)) {
							if (error.errors.length === 0) showNotification(error.message, "error");
							else
								error.errors?.forEach((e) => {
									showNotification(e.errorMessage, "error");
								});
						} else {
							showNotification(error.message, "error");
						}
					});
				}
			},
			onError(error) {
				showNotification(error.message, "error");
			},
		});

		const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
			event.stopPropagation();
			event.preventDefault();
			setSaveClicked(true);

			props.onSaved(selectionDiff);
			handleClose();
		};

		const selectionDiff = useMemo((): SelectionDiff => {
			return computeSelectionDiff(selectedTreeIds, initialServiceIds, treeNodes);
		}, [selectedTreeIds, initialServiceIds, treeNodes]);

		const handleCheckboxChange = (nodeId: string, checked: boolean) => {
			setSelectedTreeIds((prev) => {
				const next = new Set(prev);

				if (checked) {
					next.add(nodeId);
					// Wenn Gruppe: Alle Kinder hinzufügen
					const childIds = getServiceIdsInGroup(nodeId, treeNodes);
					childIds.forEach((id) => next.add(id));
				} else {
					next.delete(nodeId);
					// Wenn Gruppe: Alle Kinder entfernen
					const childIds = getServiceIdsInGroup(nodeId, treeNodes);
					childIds.forEach((id) => next.delete(id));
				}

				// Gruppen-Checkbox automatisch setzen wenn alle Kinder selected
				return syncParentChildSelection(next, treeNodes);
			});
		};

		const renderTreeItems = (nodes: ServiceTreeNode[]): React.ReactNode => {
			return nodes.map((node) => (
				<TreeItem
					key={node.id}
					itemId={node.id}
					label={
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								py: 0.5,
								fontWeight: node.type === "group" ? 600 : 400,
							}}>
							<Checkbox
								checked={selectedTreeIds.has(node.id)}
								onChange={(e) => handleCheckboxChange(node.id, e.target.checked)}
								onClick={(e) => e.stopPropagation()}
							/>
							<span>{node.label}</span>
							{node.type === "service" && "unitName" in node.data && (
								<Box
									component="span"
									sx={{
										ml: "auto",
										fontSize: "0.875rem",
										color: "text.secondary",
										display: "flex",
										gap: 1,
									}}>
									{node.data.unitName && <span>{node.data.unitName}</span>}
									<span>{formatCurrency(node.data.sellingPrice)}</span>
								</Box>
							)}
						</Box>
					}>
					{node.children && renderTreeItems(node.children)}
				</TreeItem>
			));
		};

		const columns: GridColDef<AvailableService | AvailableServiceGroup>[] = [
			{
				field: "name",
				headerName: t("listing"),
				width: 250,
				sortable: false,
				renderCell(params: GridCellParams) {
					return params.row.name;
				},
			},
			{
				field: "unit",
				headerName: t("unit"),
				width: 250,
				sortable: false,
				renderCell(params: GridCellParams) {
					if ("unitName" in params.row) return params.row.unitName;
					return "";
				},
			},
			{
				field: "sellingPrice",
				headerName: t("selling-price"),
				width: 290,
				sortable: false,
				renderCell(params: GridCellParams) {
					if ("sellingPrice" in params.row)
						return (
							<EuroField
								value={params.row.sellingPrice ?? null}
								onValueChange={() => {}}
								disabled
								variant="standard"
							/>
						);
					return "";
				},
			},
		];

		return (
			<Dialog
				open={open}
				PaperProps={{
					component: "form",
					onSubmit: handleSubmit,
				}}
				maxWidth="md"
				fullWidth>
				<DialogTitle>{t("services")}</DialogTitle>
				<DialogContent>
					<Box
						sx={{
							height: "auto",
							width: "100%",
							borderBottom: "1px solid lightgrey",
						}}>
						<SimpleTreeView sx={{ height: 400, overflowY: "auto" }}>
							{renderTreeItems(treeNodes)}
						</SimpleTreeView>
					</Box>
				</DialogContent>
				<DialogActions>
					<Box sx={{ mr: "auto", fontSize: "0.8rem", color: "text.secondary" }}>
						Add: {selectionDiff.addedServiceIds.length} | Remove:{" "}
						{selectionDiff.removedServiceIds.length}
					</Box>
					<Button data-testid="close-button" variant="outlined" onClick={handleClose}>
						{t("cancel")}
					</Button>
					<LoadingButton
						loading={saveClicked}
						data-testid="dialog-submit-team-member"
						type="submit"
						variant="contained"
						className={saveClicked ? "" : "blueButton"}>
						{t("save")}
					</LoadingButton>
				</DialogActions>
			</Dialog>
		);
	},
);

DialogSelectServices.displayName = "DialogSelectServices";
export { DialogSelectServices };
